/* eslint-disable max-lines-per-function -- Large component */
import {
  BannerType,
  CHARACTER_MODERATION_STATUS,
  CHARACTER_VISIBILITY,
  CharacterType,
  ICharacter,
  setMyChatbotSortBy,
  useAppDispatch,
  useAppSelector,
  useCms,
  useTranslation,
  useUserInfo,
  useUserSettings,
} from "@next-platform/client-common-core";
import { SubCapabilityKey, useApplicationCapabilitiesAllowed } from "@next-platform/client-common-core/infra/application/capabilities";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  CharacterCardActions,
  CharacterCardDisplayMode,
  CharacterCardVariant,
  IconVariants,
  ListEmpty,
  Spinner,
  SpinnerSize,
  useMedia,
} from "@next-platform/client-common-spicy-ui";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";

import { AgeVerificationWarningModal, CmsBanner, CmsBannerService } from "@/components";
import CharacterCard from "@/components/Character/CharacterCard/variants";
import ConfirmationModal from "@/components/common/ConfirmationModal";
import ModerationWarningModal from "@/components/common/ModerationWarningModal";
import { Row } from "@/components/customComponents";
import Trans from "@/components/Trans/Trans";
import { FilterKeys } from "@/components/type";
import useApi from "@/components/useApi";
import {
  CHATBOT_INTERACTION_EVENT_MAP,
  EXTERNAL_PERMISSIONS,
  FEATURE_FLAGS,
  getDraftStorageKey,
  RESTRICTED_MODAL,
  ROUTES,
  SPICYCHAT_DISCORD,
} from "@/constants";
import { useAgeVerificationFlags, useCenterElementDetection, useFeatureFlag, useMyChatbotsData, useQueueLogEvent, useRecombee } from "@/hooks";
import useCharacterCardGridConfig from "@/hooks/useCharacterCardGridConfig";
import useLocalizedLocation from "@/hooks/useLocalizedLocation";
import { OutletContextType } from "@/pages/Creations/CreationLayout";
import { IDraftChracter } from "@/types";
import {
  convertToSnakeCase,
  getBusinessRouteScreen,
  getCharacterCardStatusBadgeLabel,
  getCharacterCardVariantLabel,
  getDraftCardsThumbnailImage,
  getMyChatbotDropdownActionLabels,
  getNumberOfCols,
  getProfilePathForCharacter,
  syncDraftCharacters,
} from "@/utils";
import { getEmptyStateValue } from "@/utils/getEmptyStateValue";
import getShouldBlurNsfwAvatars from "@/utils/getShouldBlurNsfwAvatars";

import { CHARACTER_CARD_VARIANT } from "./constants";
import { TabNames } from "./types";

const CONTAINER_PADDING_MOBILE = 10;
const CONTAINER_PADDING_DESKTOP = 16;
const CONTAINER_WIDTH_FALLBACK = 400;

export default function ChatbotMyLoggedIn() {
  const { t } = useTranslation();
  const chatbotActionLabels = getMyChatbotDropdownActionLabels(t);
  const { hasFeatureFlag: hasVoiceAttachmentFeatureFlag } = useFeatureFlag(FEATURE_FLAGS.CHARACTER_VOICE_ATTACHMENT);
  const dispatch = useAppDispatch();
  const { pagePath } = useLocalizedLocation();
  const screenName = getBusinessRouteScreen(pagePath);
  const { logEvent } = useQueueLogEvent();
  const { isMobile } = useMedia();
  const { getInlineBannersRowMapping } = useCms();
  const { sendUserInteractionDetailView, recombeeUserId } = useRecombee();
  const { blurNSFW } = useUserSettings();
  const isNsfwUserToggleAllowed = useApplicationCapabilitiesAllowed(SubCapabilityKey.NsfwUserToggle);
  const shouldBlurNsfwAvatars = getShouldBlurNsfwAvatars({ isNsfwUserToggleAllowed, blurNSFW });
  const { callApi: callDeleteApi, isLoading: isDeleteLoading, jsonApiService } = useApi();
  const [selectedCharacterInfo, setSelectedCharacterInfo] = useState<ICharacter | null>(null);
  const [stateIsModerationModalVisible, setStateIsModerationModalVisible] = useState(false);
  const characterCardContainerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { permissions, userId } = useUserInfo();
  const sortBy = useAppSelector((state: { settings: { myChatbotSortBy: string } }) => state.settings.myChatbotSortBy);
  const [isAgeVerificationVisible, setIsAgeVerificationVisible] = useState<boolean | undefined>(undefined);
  const { isAgeVerificationRequired } = useAgeVerificationFlags();

  const {
    chatbots,
    filteredAndSortedChatbots,
    isLoading,
    isFiltering,
    isLoadingMoreGroupchats,
    shouldShowLoadMoreButton,
    localSearchQuery,
    localFilterBy,
    handleLocalSearch,
    handleLocalFilter,
    handleLocalSort,
    refetch,
    loadMoreChatbots,
  } = useMyChatbotsData({
    initialSortType: convertToSnakeCase(sortBy),
    type: CharacterType.STANDARD,
  });

  const draftKey = getDraftStorageKey(userId);
  const loadDrafts = useCallback((): Record<string, IDraftChracter> => JSON.parse(localStorage.getItem(draftKey) || "{}"), [draftKey]);

  const [draftData, setDraftData] = useState<Record<string, IDraftChracter>>(() => loadDrafts());

  useEffect(() => {
    setDraftData(loadDrafts());
  }, [loadDrafts]);

  const draftValues = useMemo(() => Object.values(draftData) as IDraftChracter[], [draftData]);
  const [stateIsShowDeleteConfirmationDialog, setStateIsShowDeleteConfirmationDialog] = useState(false);
  const [stateIsShowBanDialog, setStateIsShowBanDialog] = useState(false);
  const hasPermissionToDelete = permissions?.includes(EXTERNAL_PERMISSIONS.delete_character);
  const characterId = selectedCharacterInfo?.character_id || selectedCharacterInfo?.id;
  const { colWidth, colGap } = useCharacterCardGridConfig();
  const containerWidth = Math.max(characterCardContainerRef.current?.clientWidth || CONTAINER_WIDTH_FALLBACK, CONTAINER_WIDTH_FALLBACK);
  const containerPadding = isMobile ? CONTAINER_PADDING_MOBILE : CONTAINER_PADDING_DESKTOP;
  const numberOfColumns = useMemo(
    () =>
      getNumberOfCols({
        containerWidth,
        containerPadding,
        colWidth,
        colGap,
      }),
    [containerWidth, containerPadding, colWidth, colGap],
  );
  const { centeredElementIds, setElementRef } = useCenterElementDetection();

  useEffect(() => {
    if (chatbots?.length) {
      setDraftData((prevDraftData) => {
        const moderatedData = Object.values(prevDraftData).filter(
          (draftItem: IDraftChracter) => draftItem?.moderationReport?.moderation_status === CHARACTER_MODERATION_STATUS.FIX_REQUIRED,
        );
        if (!moderatedData.length) {
          return prevDraftData;
        }
        return syncDraftCharacters(moderatedData, chatbots, prevDraftData, draftKey);
      });
    }
  }, [chatbots, draftKey]);

  const [search, filter, sort] = useOutletContext<OutletContextType>();

  useEffect(() => {
    handleLocalSearch(search);
    handleLocalFilter(filter.key, filter.value);
    const sortType = convertToSnakeCase(sort);
    handleLocalSort(sortType);
    dispatch(setMyChatbotSortBy(sort));
  }, [search, filter.key, filter.value, sort, handleLocalSearch, handleLocalFilter, handleLocalSort, convertToSnakeCase]);

  const getCharacterInfoById = useCallback(
    (characterIdToLookup: string) => {
      let characterData;
      // only get character info when the character is different form current one
      if (characterId !== characterIdToLookup) {
        characterData = filteredAndSortedChatbots?.find((character: ICharacter) => character?.id === characterIdToLookup);

        if (characterData) {
          setSelectedCharacterInfo(characterData);
        }
      }
      return characterData || selectedCharacterInfo;
    },
    [characterId, selectedCharacterInfo, filteredAndSortedChatbots],
  );

  const handleCardClick = (characterId: string) => {
    const characterData = getCharacterInfoById(characterId);
    const moderationStatus = characterData?.moderationReport?.moderation_status;
    if (!moderationStatus || moderationStatus === CHARACTER_MODERATION_STATUS.CLOSED) {
      // if character is able to chat & it's visibility is public then only send recombee event
      if (characterData?.visibility === CHARACTER_VISIBILITY.PUBLIC) {
        const charId = (characterData?.id || characterData?.character_id) ?? "";
        void sendUserInteractionDetailView(recombeeUserId, charId);
      }
    } else {
      setStateIsModerationModalVisible(true);
    }
  };

  const handleProfileNavigation = (characterId: string) => {
    const characterData = getCharacterInfoById(characterId);
    if (!characterData) return;
    navigate(getProfilePathForCharacter(characterData));
  };

  const characterCardHandler = (isNsfw: boolean, action: CharacterCardActions, characterId: string) => {
    if (isNsfw && isAgeVerificationRequired) {
      setIsAgeVerificationVisible(true);
      return;
    }
    const botCategory = convertToSnakeCase(sortBy);
    const eventName = CHATBOT_INTERACTION_EVENT_MAP[action as keyof typeof CHATBOT_INTERACTION_EVENT_MAP];
    if (eventName) {
      logEvent(eventName, {
        bot_category: botCategory,
        character_id: characterId,
        page_name: screenName,
      });
    }
    switch (action) {
      case CharacterCardActions.CHAT:
        handleCardClick(characterId);
        break;
      case CharacterCardActions.INFO:
        handleProfileNavigation(characterId);
        break;
    }
  };

  const handleClose = () => {
    setStateIsShowDeleteConfirmationDialog(false);
    setStateIsShowBanDialog(false);
  };

  const handleDelete = (characterId: string) => {
    getCharacterInfoById(characterId);
    if (!hasPermissionToDelete) return setStateIsShowBanDialog(true);
    setStateIsShowDeleteConfirmationDialog(true);
  };

  const deleteCharacter = async (id: string) => {
    if (draftData[id]) {
      const refreshedData = { ...draftData };
      delete refreshedData[id];
      setDraftData(refreshedData);
      localStorage.setItem(draftKey, JSON.stringify(refreshedData));
    }
    await callDeleteApi(jsonApiService.deleteResource, `characters/${id}`).then(async () => {
      await refetch();
    });
  };

  const rowMapping = CmsBannerService.contentfulScreen && getInlineBannersRowMapping(CmsBannerService.contentfulScreen);
  const totalNumberOfRows = filteredAndSortedChatbots ? filteredAndSortedChatbots?.length / numberOfColumns : 0;
  const shouldShowInlineBanner = (index: number) => {
    if (!index) {
      return false;
    }
    const itemRowNumber = index / numberOfColumns;
    return totalNumberOfRows > itemRowNumber;
  };
  return (
    <>
      <div ref={characterCardContainerRef} className="w-full h-full flex flex-col">
        {isLoading || !filteredAndSortedChatbots ? (
          <div className="flex justify-center items-center w-full h-full">
            <Spinner size={SpinnerSize.LARGE} className="mx-auto" />
          </div>
        ) : (
          <>
            {isFiltering ? (
              <div className="flex justify-center items-center h-full">
                <Spinner className="mx-auto" />
              </div>
            ) : (
              <>
                {!filteredAndSortedChatbots?.length && (
                  <div className=" flex justify-center items-center h-full">
                    <ListEmpty
                      isLoading={false}
                      {...getEmptyStateValue({
                        searchedQuery: localSearchQuery,
                        filterBy: localFilterBy,
                        navigate,
                        activeTab: TabNames.CHATBOTS,
                        t,
                      })}
                    />
                  </div>
                )}
                {(!!draftValues?.length || !!filteredAndSortedChatbots?.length) && (
                  <div
                    className="mt-lg grid !gap-y-3 pb-4 max-mob:pb-2"
                    style={{
                      gridTemplateColumns: `repeat(auto-fill, minmax(${colWidth}px, 1fr))`,
                      gap: colGap,
                    }}
                  >
                    {!!draftValues?.length && !localSearchQuery && localFilterBy === FilterKeys.ALL && (
                      <CharacterCard
                        variant={CharacterCardVariant.DRAFTS}
                        characterInfo={{
                          characterDraftCount: draftValues?.length,
                          characterDraftImages: getDraftCardsThumbnailImage(draftValues),
                          draftsPagePath: ROUTES.chatbotsDrafts,
                          characterId: "",
                          characterName: "",
                          characterCreatorUserName: "",
                          characterTitle: "",
                          characterMessageNumber: 0,
                          characterAvatarUrl: "",
                          draftsCountLabel: t("myCreation:page.draftsCount", { count: draftValues?.length }),
                        }}
                      />
                    )}
                    {(filteredAndSortedChatbots || [])?.map((characterInfo: ICharacter, idx: number) => {
                      const moderationStatus = characterInfo?.moderationReport?.moderation_status;
                      const activeModerationStatus = moderationStatus === CHARACTER_MODERATION_STATUS.CLOSED ? undefined : moderationStatus;
                      const manualReviewRequired = characterInfo.aicq_manual_review_required
                        ? CHARACTER_MODERATION_STATUS.MANUAL_REVIEW_REQUIRED
                        : "";
                      const key = (manualReviewRequired ||
                        activeModerationStatus ||
                        characterInfo?.visibility) as keyof typeof CHARACTER_CARD_VARIANT;
                      const variant = CHARACTER_CARD_VARIANT[key];
                      const variantLabel = getCharacterCardVariantLabel(variant, t);
                      const statusBadgeLabel = getCharacterCardStatusBadgeLabel(variant, t);
                      const isCharacterAvatarBlur = shouldBlurNsfwAvatars && characterInfo?.avatar_is_nsfw;

                      return (
                        <React.Fragment key={characterInfo?.id}>
                          {rowMapping?.[idx / numberOfColumns]?.length && shouldShowInlineBanner(idx) ? (
                            <div className=" w-full col-span-full">
                              <CmsBanner
                                placement={BannerType.Inline}
                                isVisibleOnPartner={false}
                                availableBanners={rowMapping?.[idx / numberOfColumns]}
                              />
                            </div>
                          ) : null}
                          <div ref={(el: HTMLDivElement | null) => setElementRef(characterInfo?.id, el)}>
                            <CharacterCard
                              characterInfo={{
                                characterId: characterInfo?.id,
                                characterName: characterInfo?.name,
                                characterCreatorUserName: characterInfo?.creator_username,
                                characterTitle: characterInfo?.title,
                                characterTags: characterInfo?.tags,
                                characterMessageNumber: characterInfo?.num_messages || "0",
                                characterRatingScore: characterInfo?.rating_score,
                                characterAvatarUrl: characterInfo?.avatar_url ?? "",
                                characterDefinitionVisible: characterInfo?.definition_visible,
                                characterInfoLabel: `${characterInfo?.definition_visible ? t("chatbotCard:tooltip.definitionVisible") : t("chatbotCard:tooltip.definitionHidden")}`,
                                characterTokensCount: characterInfo?.token_count,
                                characterImageGen: characterInfo?.lora_status === "ready",
                                hasVoice: hasVoiceAttachmentFeatureFlag && !!characterInfo?.voice_id,
                                creatorPagePath: `/creator/${characterInfo?.creator_username}`,
                                chatPagePath:
                                  (moderationStatus && moderationStatus !== CHARACTER_MODERATION_STATUS.CLOSED) ||
                                  (characterInfo?.is_nsfw && isAgeVerificationRequired)
                                    ? undefined
                                    : `/chat/${characterInfo?.id}`,
                                characterAvatarBlur: isCharacterAvatarBlur,
                                characterAicq: characterInfo.aicq_manual_review_required === true,
                                lorebookAttached: characterInfo?.has_lorebooks || false,
                                lorebookLabel: characterInfo?.has_lorebooks ? t("chatbotCard:tooltip.lorebookAttached") : undefined,
                                variantLabel,
                                statusBadgeLabel,
                              }}
                              characterCardActionsHandler={(action: CharacterCardActions, characterId: string) =>
                                characterCardHandler(characterInfo?.is_nsfw, action, characterId)
                              }
                              characterCardActionsState={{
                                [CharacterCardActions.FAVORITE]: {
                                  isHidden: true,
                                },
                              }}
                              variant={variant}
                              displayMode={CharacterCardDisplayMode.STATUS}
                              dropdownMenuActions={[
                                {
                                  label: chatbotActionLabels.edit,
                                  icon: IconVariants.EDIT,
                                  to: `${ROUTES.editChatbot}/${characterInfo?.id}`,
                                  onPress: () => {},
                                },
                                {
                                  label: chatbotActionLabels.delete,
                                  icon: IconVariants.TRASH,
                                  onPress: () => handleDelete(characterInfo?.id),
                                  isDisabled: false,
                                  isHidden: false,
                                  className: "text-red-9",
                                },
                                {
                                  label: chatbotActionLabels.report,
                                  icon: IconVariants.FLAG,
                                  to: `${ROUTES.report}/${characterInfo.id}`,
                                  onPress: () => {},
                                  isDisabled: false,
                                  isHidden: false,
                                },
                              ]}
                              atCenter={centeredElementIds.includes(characterInfo?.id)}
                            />
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </div>
                )}
                {shouldShowLoadMoreButton ? (
                  <Row justify="center" className="my-lg">
                    <Button
                      loading={isLoadingMoreGroupchats}
                      onClick={() => void loadMoreChatbots()}
                      size={ButtonSize.LARGE}
                      variant={ButtonVariant.OUTLINED}
                      data-testid="ChatbotMy-LoadMoreButton"
                    >
                      <Trans i18nKey="common:loadMore" />
                    </Button>
                  </Row>
                ) : null}
              </>
            )}
          </>
        )}
      </div>
      {stateIsModerationModalVisible ? (
        <ModerationWarningModal
          reason_for_moderation=""
          moderationModalVisible={stateIsModerationModalVisible}
          setModerationModalVisible={setStateIsModerationModalVisible}
          character={selectedCharacterInfo!}
        />
      ) : null}
      {stateIsShowDeleteConfirmationDialog ? (
        <ConfirmationModal
          heading={t("myCreation:modal.removeChatbot.title")}
          handleClose={handleClose}
          isLoading={isDeleteLoading}
          onUserConfirmation={() => deleteCharacter(characterId!)}
          confirmationMessage={t("myCreation:modal.removeChatbot.description", { name: selectedCharacterInfo?.name })}
          isVisible={stateIsShowDeleteConfirmationDialog}
          actionButtonColor="error"
          actionButtonText={t("common:remove")}
          discardButtonText={t("common:cancel")}
        />
      ) : null}
      {stateIsShowBanDialog ? (
        <ConfirmationModal
          handleClose={handleClose}
          heading={RESTRICTED_MODAL?.title}
          confirmationMessage={RESTRICTED_MODAL?.confirmationMessage}
          link={SPICYCHAT_DISCORD}
          linkText={RESTRICTED_MODAL?.contactUsLinkText}
          isVisible={stateIsShowBanDialog}
          isLoading={false}
          showButtons={false}
        />
      ) : null}
      <AgeVerificationWarningModal version="chat" isOpen={isAgeVerificationVisible} onClose={() => setIsAgeVerificationVisible(false)} />
    </>
  );
}
